import { useHandleResponse } from '@/utils/useHandleResponse';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { App, Badge, Button, Col, Input, Modal, Radio, Row, Select, Spin, Table, Tabs, Tag, Upload } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import { debounce } from 'lodash';
import moment from 'moment';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from '../../../../context/AuthProvider';
import CompanyService from '../../../../services/company/CompanyService';
import InterventionFileService from '../../../../services/interventionFile/InterventionFileService';
import SystemTypeService from '../../../../services/systemType/SystemTypeService';
import { RootState } from '../../../../store';
import { getBaseUrl } from '../../../../utils/baseUrl';
import request from '../../../../utils/request';
import { saveBlobCrossPlatform } from '../../../../utils/saveBlobCrossPlatform';
import { updateProps } from '../../.././../contextRedux/InterventionFile/InterventionFileModel';

const InterventionFileSupplies: React.FC = () => {
  const dispatch = useDispatch();

  const searchTerm = useSelector((state: RootState) => state.models?.interventionFile.props.searchTerm);
  const searchTermType = useSelector((state: RootState) => state.models?.interventionFile.props.searchTermType);

  const [uploadVisible, setUploadVisible] = useState(false);
  const [currentRecord, setCurrentRecord] = useState<any | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  const [fileList, setFileList] = useState<any[]>([]);
  const [interventionFiles, setInterventionFiles] = useState<any[]>([]);

  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [selectedWorkPlace, setSelectedWorkPlace] = useState<any>(null);
  const [selectedSystemType, setSelectedSystemType] = useState<any>(null);

  const [activeKey, setActiveKey] = useState('Noi venite');
  const { profile } = useAuth();
  const { modal, message } = App.useApp();
  const handleResponse = useHandleResponse();

  const {
    data: _data,
    isFetching: isInterventionFilesLoading,
    refetch,
  } = useQuery({
    queryKey: ['interventionFiles', searchTerm, searchTermType, selectedCompany, selectedWorkPlace, selectedSystemType],
    queryFn: () =>
      InterventionFileService.search({
        isInterventionFileSystemTypeIncluded: true,
        isInterventionFileInvoiceFilesIncluded: true,
        isInterventionFileEngineerIncluded: true,
        isInterventionFilePerformOperationsIncluded: true,
        isInterventionFileEquipmentsIncluded: true,
        isMentenancePlanningIncluded: true,
        isCompanyIncluded: true,
        isWorkPlaceIncluded: true,
        isAgentIncluded: true,
        isStatusIncluded: true,
        hasConsumptionDetails: true,
        termen: searchTerm || undefined,
        termenType: searchTermType || undefined,
        companyName: selectedCompany || undefined,
        workPlaceName: selectedWorkPlace || undefined,
        systemTypeName: selectedSystemType || undefined,
      }).then((data) => {
        setInterventionFiles(data?.response?.filter((data: any) => data.isDraft != true));
        return data?.response;
      }),
    enabled: !!activeKey,
    refetchOnWindowFocus: false,
  });

  const { data: companies, isFetching: isCompaniesLoading } = useQuery({
    queryKey: ['companies'],
    queryFn: () =>
      CompanyService.search({
        isAgentIncluded: true,
        isWorkPlacesIncluded: true,
        isAccountManagerIncluded: true,
        isActive: true,
      }).then((data) => {
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: systemTypes, isFetching: isSystemTypesLoading } = useQuery({
    queryKey: ['systemTypes'],
    queryFn: () =>
      SystemTypeService.search({
        isDeviceTypeIncluded: true,
        isActive: true,
      }).then((data) => {
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const workPlaces = useMemo(() => {
    if (!selectedCompany) return [];
    const company = companies?.find((c: any) => c.name === selectedCompany);
    return company?.workPlaces || [];
  }, [companies, selectedCompany]);

  const { mutate: donwloadFile, isPending: isDownloadInvoiceFile } = useMutation({
    mutationFn: (fileId: any) => InterventionFileService.downloadFile(fileId),
    onSuccess: (response: any) => {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'file.pdf';
      if (contentDisposition) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }
      return response.blob().then((blob: any) => {
        saveBlobCrossPlatform(blob, filename);
      });
    },
    onError: (error: any) => {
      message.error('Eroare procesare request: ' + error.message);
    },
  });
  const { mutate: saveInterventionFile, isPending: isSavingInterventionFile } = useMutation({
    mutationFn: (formData: any) => InterventionFileService.upsert(formData.id, formData),
    onSuccess: (data: any) => {
      if (!data) {
        console.error('Received data is not in JSON format or JSON parsing failed.');
        return;
      }
      handleResponse(data, data?.response?.number.toString());
      refetch();
    },
    onError: (error: any) => {
      message.error(`Error processing your request: ${error.message}`);
    },
  });
  const { mutate: printInterventionFile, isPending: isPrinting } = useMutation<
    Response, // Type of the success response
    Error, // Type of the error
    { record: any; action: 'view' | 'download' } // Type of the variables passed to mutate
  >({
    mutationFn: async ({ record, action }) => {
      const fileUrl = `${getBaseUrl()}/interventionFile/files/${record.id}`;
      if (action === 'view') {
        // Open the file directly in the browser
        window.open(fileUrl, '_blank');
        return new Response(null, { status: 200 }); // Return a dummy response since we are redirecting
      }
      // If action is 'download', fetch the file
      return await InterventionFileService.print(record.id);
    },
    onSuccess: async (response, { action }) => {
      if (!response.ok) {
        throw new Error(`Network response was not ok: ${response.statusText}`);
      }
      if (action === 'download') {
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'file.pdf';
        if (contentDisposition) {
          const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
          const matches = filenameRegex.exec(contentDisposition);
          if (matches != null && matches[1]) {
            filename = matches[1].replace(/['"]/g, '');
          }
        }
        const blob = await response.blob();
        saveBlobCrossPlatform(blob, filename);
      }
    },
    onError: (error) => {
      console.error('Error processing your request:', error);
    },
  });
  // Wrapper function to call mutate with 'view' action
  const preview = (record: any) => {
    // In APK mode, treat 'view' as 'download' since APK can't handle browser previews
    const w = window as any;
    const action = w.AndroidInterface && typeof w.AndroidInterface.saveBlobFile === 'function' ? 'download' : 'view';
    printInterventionFile({ record, action });
  };
  const uploadFiles = async (files: File[]): Promise<any[]> => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    try {
      const response = await request(
        `/interventionFile/upload`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            Authorization: 'Bearer ' + localStorage.getItem('authToken'),
          },
          body: formData,
        },
        true
      );
      const data = await response.json();
      if (data?.errors?.length > 0) {
        message.error(data?.errors[0]);
        return [];
      } else {
        message.success(`Fisierul a fost incarcat cu success!`);
        return data?.response;
      }
    } catch (error: any) {
      message.error(`Incarcare nereusita: ${error.message}`);
      return [];
    }
  };
  const handleDownloadFile = async (file: any) => {
    const interventionFile = interventionFiles.find((f: any) => f.id == file.interventionFileId);
    let matchingFile = interventionFile.interventionFileInvoiceFiles.find((i: any) => i.fileName === file.fileName);

    if (matchingFile) {
      const formData = new FormData();
      formData.append('fileName', matchingFile.fileName);
      formData.append('fileData', matchingFile.filePath);
      formData.append('fileType', matchingFile.fileExtension);
      donwloadFile(formData); // Pass formData here
    } else {
      message.error('File not found.');
    }
  };
  const handleStatusChange = (record: any) => {
    if (record.statusId === 2) {
      let selectedOption = '';
      // Modal for status 2 to 3 transition
      modal.confirm({
        title: 'Selecteaza metoda de acceptare',
        content: (
          <Radio.Group onChange={(e) => (selectedOption = e.target.value)}>
            <Radio value='Acceptat SICAP'>Acceptat SICAP</Radio>
            <Radio value='Acceptat Direct'>Acceptat Direct</Radio>
          </Radio.Group>
        ),
        onOk: () => {
          if (!selectedOption) {
            message.error('Va rugam sa selectati o optiune!');
            return;
          }
          const updatedRecord = {
            ...record,
            statusId: 3,
            status: { name: 'Acceptata' },
            changes: [
              ...(record?.changes || []),
              `[${profile?.name}] Acceptata (${selectedOption}) - ${new Date().toLocaleString()}`,
            ],
          };
          saveInterventionFile(updatedRecord);
        },
      });
    } else if (record.statusId === 3) {
      // Existing logic for uploading files for status 3 to 4
      setUploadedFiles([]);
      setFileList([]);
      setCurrentRecord(record);
      setUploadVisible(true);
    } else {
      if (record.statusId == 1) {
        record.statusId = 2;
        record.changes = [...(record?.changes || []), `[${profile?.name}] Procesata ${new Date().toLocaleString()}`];
        saveInterventionFile(record);
      }
    }
  };
  const handleUploadOk = async () => {
    if (uploadedFiles.length === 0) {
      message.error('Va rog sa incarcati minim un fisier.');
      return;
    }
    const uploadedFileNames = uploadedFiles.map((file) => file.fileName).join(', ');
    const updatedRecord = {
      ...currentRecord,
      interventionFileInvoiceFiles: [...(currentRecord?.interventionFileInvoiceFiles || []), ...uploadedFiles],
      statusId: 4, // Move to Finalizate
      status: { name: 'Finalizata' },
      changes: [
        ...(currentRecord?.changes || []),
        `[${profile?.name}] Fisiere incarcate (${uploadedFileNames}) - ${new Date().toLocaleString()}`,
      ],
    };
    await saveInterventionFile(updatedRecord);
    setUploadedFiles([]);
    setFileList([]);
    setCurrentRecord(null);
    setUploadVisible(false);
  };
  const handleCancel = () => {
    setUploadedFiles([]);
    setFileList([]);
    setUploadVisible(false);
    setCurrentRecord(null);
  };

  // First, update the INVOICE_OPTIONS constant with colors and icons
  const INVOICE_OPTIONS = [
    {
      label: 'Factura Trimisa',
      value: 'isInvoiceSent',
      color: '#52c41a', // green
      description: 'Factura a fost trimisa catre client',
    },
    {
      label: 'Nu se factureaza',
      value: 'isNotInvoiced',
      color: '#f5222d', // red
      description: 'Aceasta interventie nu necesita facturare',
    },
    {
      label: 'Garantie',
      value: 'isGuarantee',
      color: '#1890ff', // blue
      description: 'Interventie in perioada de garantie',
    },
    {
      label: 'Gratis',
      value: 'isFree',
      color: '#722ed1', // purple
      description: 'Interventie gratuita',
    },
  ];

  // Update the handleInvoiceToggle function
  const handleInvoiceToggle = (record: any, key: string) => {
    const selectedOption = INVOICE_OPTIONS.find((opt) => opt.value === key);

    const updatedRecord = {
      ...record,
      changes: [
        ...(record?.changes || []),
        `[${profile?.name}] Status facturare schimbat in ${selectedOption?.label} - ${new Date().toLocaleString()}`,
      ],
    };

    // Reset all invoice statuses first
    INVOICE_OPTIONS.forEach((option) => {
      updatedRecord[option.value] = false;
    });

    // Set the selected status
    updatedRecord[key] = true;

    const updatedRecords = interventionFiles.map((file: any) => (file.id === record.id ? updatedRecord : file));
    setInterventionFiles(updatedRecords);
    saveInterventionFile(updatedRecord);
  };
  const handleViewChanges = (record: any) => {
    modal.info({
      title: `Modificari si fisiere pentru fisa cu numarul: ${record.number}`,
      content: (
        <div>
          <h3>Modificari:</h3>
          {record?.changes?.length > 0 ? (
            record.changes.map((change: any, index: any) => (
              <p key={index}>
                {index + 1}. {change}
              </p>
            ))
          ) : (
            <p>Inca nu au fost facute modificari.</p>
          )}
          <h3>Fisiere Incarcate:</h3>
          {record?.interventionFileInvoiceFiles?.length > 0 ? (
            record.interventionFileInvoiceFiles.map((file: any, index: number) => (
              <Button
                key={file?.id || index}
                type='link'
                icon={<DownloadOutlined />}
                onClick={() => handleDownloadFile(file)}
              >
                {file?.fileName}
              </Button>
            ))
          ) : (
            <p>Niciun fisier incarcat.</p>
          )}
        </div>
      ),
    });
  };

  const handleCompanyChange = (value: string | null) => {
    setSelectedCompany(value);
    dispatch(updateProps({ field: 'skip', value: 0 }));
  };

  const handleWorkPlaceChange = (value: string | null) => {
    setSelectedWorkPlace(value);
    dispatch(updateProps({ field: 'skip', value: 0 }));
  };

  const handleSystemTypeChange = (value: string | null) => {
    setSelectedSystemType(value);
    dispatch(updateProps({ field: 'skip', value: 0 }));
  };

  const handleSearch = useCallback(
    (value: string) => {
      debouncedSearch(value, 'searchTerm');
    },
    [searchTerm]
  );

  const handleTypeChange = useCallback(
    (value: string) => {
      debouncedSearch(value, 'searchTermType');
    },
    [searchTermType]
  );

  const debouncedSearch = useCallback(
    debounce((value: string, search: string) => {
      dispatch(updateProps({ field: search, value: value }));
      dispatch(updateProps({ field: 'skip', value: 0 }));
    }, 1000),
    []
  );
  const uploadProps: any = {
    multiple: true,
    showUploadList: true,
    fileList,
    customRequest: async ({ onSuccess, onError, file }: any) => {
      try {
        const uploadedFile = await uploadFiles([file]);
        if (uploadedFile.length > 0) {
          const newFile = {
            id: '',
            interventionFileId: currentRecord.id,
            fileName: file.name,
            fileExtension: file.type,
            fileContent: uploadedFile,
            filePath: uploadedFile,
          };
          setUploadedFiles((prev) => [...prev, newFile]);
          setFileList((prev) => [...prev, file]);
          onSuccess('ok');
        } else {
          onError('File upload failed');
        }
      } catch (error) {
        console.error('Upload error:', error);
        onError(error);
      }
    },
    beforeUpload: (file: any) => {
      const isAllowedType = [
        'image/jpg',
        'image/jpeg',
        'image/png',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ].includes(file.type);
      if (!isAllowedType) {
        message.error('You can only upload JPG/PNG images or PDF/DOC files!');
        return Upload.LIST_IGNORE;
      }
      return true;
    },
    itemRender: (_originNode: React.ReactNode, file: UploadFile) => {
      return (
        <div className='file-actions'>
          <div className='download-action-button'>
            <Button
              type='link'
              icon={<DownloadOutlined />}
              onClick={() => handleDownloadFile(file)}
              disabled={currentRecord.statusId === 3}
            >
              {file.name}
            </Button>
          </div>
          <div className='delete-action-button'>
            <Button
              type='link'
              danger
              onClick={() => {
                // Remove file from state
                setUploadedFiles((prev) => prev.filter((f) => f.fileName !== file.name));
                setFileList((prev) => prev.filter((f) => f.name !== file.name));
                message.success(`File "${file.name}" removed successfully.`);
              }}
            >
              Sterge
            </Button>
          </div>
        </div>
      );
    },
  };
  const columns = useMemo(() => {
    const baseColumns = [
      {
        title: 'Numar',
        dataIndex: '',
        key: '',
        render: (record: any) => (
          <Tag color={'blue'} style={{ cursor: 'pointer' }} onClick={() => preview(record)}>
            {record?.number}
          </Tag>
        ),
      },
      {
        title: 'Tip',
        dataIndex: 'type',
        key: 'type',
      },
      {
        title: 'Firma',
        key: 'company',
        render: (record: any) => {
          const company = record?.metadata?.company ?? record?.company;
          return (
            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: 200 }}>
              {company?.name?.length > 15 ? company.name : company?.name}
            </div>
          );
        },
      },
      {
        title: 'Locatie',
        dataIndex: '',
        key: 'workPlace',
        render: (record: any) => (
          <>
            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: 300 }}>
              <b>
                {record?.metadata?.workPlace?.name?.length ?? record?.workPlace?.name?.length > 30
                  ? record?.metadata?.workPlace?.name ?? record?.workPlace?.name
                  : record?.metadata?.workPlace?.name ?? record?.workPlace?.name}
              </b>
            </div>
            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: 300 }}>
              {record?.metadata?.address?.length ?? record?.address?.length > 30
                ? record?.metadata?.workPlace?.address ?? record?.workPlace?.address
                : record?.metadata?.workPlace?.address ?? record?.workPlace?.address}
            </div>
          </>
        ),
      },
      {
        title: 'Periodicitate',
        dataIndex: '',
        key: '',
        render: (interventionFile: any) => (
          <>
            <div style={{ textAlign: 'center' }}>
              <b>
                {interventionFile?.mentenancePlanning != undefined
                  ? interventionFile?.mentenancePlanning?.periodicity
                  : interventionFile?.requestFileId != undefined
                  ? 'solicitare'
                  : ' - '}
              </b>
            </div>
          </>
        ),
      },
      {
        title: 'Sistem',
        dataIndex: 'interventionFileSystemTypes',
        key: 'interventionFileSystemTypes',
        render: (interventionFileSystemType: any[]) => (
          <>
            {interventionFileSystemType && interventionFileSystemType.length > 0 && (
              <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: 200 }}>
                <b>
                  {interventionFileSystemType
                    .map((type: any) =>
                      type.systemType.name.length > 15 ? type.systemType.name : type.systemType.name
                    )
                    .map((name: string) => `[${name}]`)
                    .join('')}
                </b>
              </div>
            )}
          </>
        ),
      },
      {
        title: 'Tehnician',
        dataIndex: 'helionEngineers',
        key: 'helionEngineers',
        render: (helionEngineers: string[]) => (
          <>
            {helionEngineers?.length > 0 ? (
              helionEngineers?.map((engineer: any, index: any) => {
                return <div key={index}>{'[' + engineer?.user?.name + ']'}</div>;
              })
            ) : (
              <div>----</div>
            )}
          </>
        ),
      },
      {
        title: 'Data Semnare',
        dataIndex: 'deliveryDate',
        key: 'deliveryDate',
        render: (deliveryDate: string) => moment(deliveryDate).format('YYYY-MM-DD'),
      },
      {
        title: 'Consumabile',
        dataIndex: 'consumptionDetails',
        key: 'consumptionDetails',
        render: (text: any) => (
          <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: 300 }}>
            {text?.length > 30 ? text : text}
          </div>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (status: any['status']) => <Tag>{status?.name}</Tag>,
      },
      {
        title: 'Actiuni',
        key: 'actions',
        render: (_: any, record: any) =>
          record.statusId == 4 ? (
            record.isInvoiceSent ? (
              <Tag color={'blue'}>Factura Trimisa</Tag>
            ) : (
              <Tag color={'purple'}>Factura Netrimisa</Tag>
            )
          ) : (
            <Button type='primary' onClick={() => handleStatusChange(record)}>
              {record.statusId === 1
                ? 'Prelueaza'
                : record.statusId === 2
                ? 'Proceseaza'
                : record.statusId === 3
                ? 'Finalizeaza'
                : 'Finalizata'}
            </Button>
          ),
      },
      {
        title: 'Istoric modificari',
        key: 'changes',
        render: (_: any, record: any) => <Button onClick={() => handleViewChanges(record)}>Istoric</Button>,
      },
    ];

    // Add 'Factura' column only if activeKey is 'Finalizate'
    if (activeKey === 'Finalizate' && profile?.userPermission?.rols?.includes('accountant')) {
      baseColumns.push({
        title: 'Status Facturare',
        key: 'invoiceStatus',
        render: (_: any, record: any) => {
          if (record.statusId !== 4) return <></>;

          // Find current status
          const currentStatus = INVOICE_OPTIONS.find((option) => record[option.value]);

          if (currentStatus) {
            // Show selected status as a tag with option to change
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Tag color={currentStatus.color} style={{ padding: '4px 8px', fontSize: '14px' }}>
                  {currentStatus.label}
                </Tag>
                <Button
                  type='link'
                  size='small'
                  onClick={() => {
                    modal.confirm({
                      title: 'Schimba status facturare',
                      content: (
                        <div>
                          <p>
                            Status curent: <Tag color={currentStatus.color}>{currentStatus.label}</Tag>
                          </p>
                          <p>Esti sigur ca vrei sa schimbi statusul?</p>
                        </div>
                      ),
                      onOk: () => {
                        // Reset all statuses
                        const updatedRecord = { ...record };
                        INVOICE_OPTIONS.forEach((option) => {
                          updatedRecord[option.value] = false;
                        });
                        saveInterventionFile(updatedRecord);
                      },
                    });
                  }}
                >
                  Schimba
                </Button>
              </div>
            );
          }

          // Show dropdown for selecting status
          return (
            <Select
              placeholder='Selecteaza status facturare'
              style={{ width: '100%' }}
              onChange={(value: string) => handleInvoiceToggle(record, value)}
              dropdownRender={(menu) => (
                <div>
                  {menu}
                  <div style={{ padding: '8px', borderTop: '1px solid #e8e8e8' }}>
                    <small style={{ color: '#888' }}>Selectati statusul potrivit pentru aceasta interventie</small>
                  </div>
                </div>
              )}
            >
              {INVOICE_OPTIONS.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div>
                      <Tag color={option.color}>{option.label}</Tag>
                    </div>
                    <small style={{ color: '#888' }}>{option.description}</small>
                  </div>
                </Select.Option>
              ))}
            </Select>
          );
        },
      });
    }

    return baseColumns;
  }, [interventionFiles, activeKey]); // Added activeKey as a dependency to ensure the columns are updated when activeKey changes

  if (
    isInterventionFilesLoading ||
    isSavingInterventionFile ||
    isDownloadInvoiceFile ||
    isCompaniesLoading ||
    isSystemTypesLoading ||
    isPrinting
  ) {
    return <Spin className='spinner' size='large' />;
  }

  const tabsData = {
    'Noi venite': interventionFiles?.filter((file: any) => file?.statusId === 1),
    'In curs': interventionFiles?.filter((file: any) => file?.statusId === 2),
    Acceptate: interventionFiles?.filter((file: any) => file?.statusId === 3),
    Finalizate: interventionFiles?.filter((file: any) => file?.statusId === 4),
  };
  const tabItems: any = Object.entries(tabsData).map(([key, data]) => ({
    key,
    label: (
      <>
        {key} <Badge count={data.length} color='blue' />
      </>
    ),
    children: (
      <Table
        dataSource={data}
        columns={columns}
        rowKey='id'
        pagination={{ pageSize: 10 }}
        scroll={{ x: 'max-content' }}
      />
    ),
  }));

  return (
    <div>
      <h1>Consum</h1>
      <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Input.Search
            placeholder='Search...'
            defaultValue={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            autoFocus={true}
            allowClear
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Select
            placeholder='Filtreaza dupa tip'
            value={searchTermType || undefined}
            onChange={handleTypeChange}
            style={{ width: '100%' }}
            allowClear
          >
            <Select.Option value='Service [cu contract]'>Service [cu contract]</Select.Option>
            <Select.Option value='Service [fara contract]'>Service [fara contract]</Select.Option>
            <Select.Option value='Garantie'>Garantie</Select.Option>
            <Select.Option value='Mentenanta periodica'>Mentenanta periodica</Select.Option>
          </Select>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Select
            placeholder='Filtreaza dupa beneficiar'
            value={selectedCompany}
            onChange={handleCompanyChange}
            style={{ width: '100%' }}
            allowClear
            showSearch
            optionFilterProp='children'
          >
            {companies.map((company: any) => (
              <Select.Option key={company?.id} value={company?.name}>
                {company?.name}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Select
            placeholder='Filtreaza dupa punct de lucru'
            value={selectedWorkPlace}
            onChange={handleWorkPlaceChange}
            style={{ width: '100%' }}
            allowClear
            showSearch
            optionFilterProp='children'
          >
            {workPlaces.map((workPlace: any) => (
              <Select.Option key={workPlace?.id} value={workPlace?.name}>
                {workPlace?.name}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Select
            placeholder='Filtreaza dupa sistem'
            value={selectedSystemType}
            onChange={handleSystemTypeChange}
            style={{ width: '100%' }}
            allowClear
            showSearch
            optionFilterProp='children'
          >
            {systemTypes.map((systemType: any) => (
              <Select.Option key={systemType?.id} value={systemType?.name}>
                {systemType?.name}
              </Select.Option>
            ))}
          </Select>
        </Col>
      </Row>
      <Tabs activeKey={activeKey} onChange={(key) => setActiveKey(key)} items={tabItems} />

      <Modal
        title='Incarcati fisierele pentru finalizare'
        open={uploadVisible}
        onOk={handleUploadOk}
        onCancel={handleCancel}
      >
        <Upload {...uploadProps}>
          <Button icon={<UploadOutlined />}>Selectati fisierele</Button>
        </Upload>
      </Modal>
    </div>
  );
};
export default InterventionFileSupplies;
